import { Button, Dropdown, StoreSelect, TextInput } from 'components/common'
import useQuery from 'hooks/useQuery'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { fetchFreshReportCategories } from 'utils/api/freshReportApi'
import { filterEmptyValues } from 'utils/helpers'
import { FILTER_FIELD_FRESH_REPORT, FRESH_REPORT_FORM_FIELDS } from './utils'

const { STORE, CATEGORY, DATE_FROM, DATE_TO } = FRESH_REPORT_FORM_FIELDS

type Props = {
  toggleFilterForm: () => void
}

const FreshReportFilterForm = ({ toggleFilterForm }: Props) => {
  const { getQuery, setMultipleQueries } = useQuery()
  const filters = getQuery(FILTER_FIELD_FRESH_REPORT)

  const [categoryOptions, setCategoryOptions] = useState<
    { label: string; value: string; disabled: boolean }[]
  >([])

  const { register, handleSubmit, setValue, reset } = useForm({
    defaultValues: filters,
  })

  useEffect(() => {
    fetchFreshReportCategories()
      .then(({ data }) => {
        setCategoryOptions(
          (data?.categories ?? []).map(
            (category: { _id: string; name: string; isActive: boolean }) => ({
              // Inactive categories still hold a month of history, so they
              // stay selectable - just labelled so it is not confusing
              label: category.isActive
                ? category.name
                : `${category.name} (inactive)`,
              value: category._id,
              disabled: false,
            }),
          ),
        )
      })
      .catch(() => setCategoryOptions([]))
  }, [])

  const onFilterApply = (data: any) => {
    const applied = filterEmptyValues(data)

    // The API requires a store - it is the leading key of the report index,
    // and without it a single day across every store is six figures of rows.
    if (!applied?.[STORE.key]) {
      return toast.error('Please select a store to view the report.', {
        duration: 3000,
      })
    }

    if (
      applied[DATE_FROM.key] &&
      applied[DATE_TO.key] &&
      applied[DATE_FROM.key] > applied[DATE_TO.key]
    ) {
      return toast.error('From Date cannot be after To Date.', {
        duration: 3000,
      })
    }

    setMultipleQueries({
      [FILTER_FIELD_FRESH_REPORT]: applied,
      page: 1,
    })

    toggleFilterForm()
  }

  const cancelFilter = () => {
    reset(filters)
    toggleFilterForm()
  }

  return (
    <div className="mt-2 rounded-lg border bg-white px-5 py-3">
      <div className="mx-auto max-w-5xl">
        <form onSubmit={handleSubmit(onFilterApply)}>
          <div className="grid grid-cols-2 gap-x-14 gap-y-3 md:grid-cols-2">
            <StoreSelect
              required
              setValue={setValue}
              name={STORE.key}
              label={STORE.label}
              defaultValue={filters?.[STORE.key]}
            />

            <Dropdown
              label={CATEGORY.label}
              name={CATEGORY.key}
              setValue={setValue}
              defaultValue={filters?.[CATEGORY.key]}
              options={categoryOptions}
            />

            <TextInput
              type="date"
              register={register}
              name={DATE_FROM.key}
              label={DATE_FROM.label}
            />

            <TextInput
              type="date"
              register={register}
              name={DATE_TO.key}
              label={DATE_TO.label}
            />
          </div>

          <p className="mt-3 text-sm text-gray-500">
            Records are kept for one month. Anything older is removed
            automatically and cannot be reported on.
          </p>

          <div className="mt-5 flex w-72 gap-x-4">
            <Button type="submit" buttonText="Apply" className="w-full" />
            <Button
              type="button"
              className="w-full"
              variant="outline"
              buttonText="Cancel"
              onClick={cancelFilter}
            />
          </div>
        </form>
      </div>
    </div>
  )
}

export default FreshReportFilterForm
